import * as React from 'react';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';
import { makeStyles, Theme } from '@material-ui/core';
import classNames from 'classnames';
import { isArray, isEmpty, uniqueId } from 'lodash';
import { NFTImage } from 'types/NftTypes';

import useAddress from '../../hooks/use-address';
import useNetwork from '../../hooks/use-network';
import useSafe from '../../hooks/use-safe';
import ArrowDown from '../../images/ArrowDownSmallIcon.svg';
import DeleteIcon from '../../images/delete_icon.svg';
import LoadingSpinner from '../../images/loading-gif.gif';
import NftImagePlaceholder from '../../images/nftImagePlaceholder.svg';
import { useNftImages } from '../../store';
import { AddressBalance, getAccountBalances } from '../../utils';
import {
  getAllCollectionNftIds,
  getCollectionPaginatedNFTsData,
  getNftImageById,
} from '../../utils/nft-tools';
import { BATCH_TRANSFER_DISABLED } from '../Dialog/TransactionDialog/WidthdrawNftDialog/constants';
import { ImageWithPlaceholder } from '../ImageWithPlaceholder/ImageWithPlaceholder';
import { InputField } from '../InputField/InputField';

import { useInputAndDisplayNftsStyles } from './useInputAndDisplayNftsStyles';

type NftItemType = {
  nftId: string;
  collectionContract: string;
  onDeleteClick: (evt: any) => void;
  isWrapperOpened: boolean;
};

type NftItemOnlyOneType = {
  nftId: string;
  collectionContract: string;
  onDelete: (evt: any) => void;
};

const ITEMS_PER_PAGE = 10;

// TODO_R: move into InputAndDisplayNfts/components
const NftIdItem = ({ nftId, collectionContract, onDeleteClick, isWrapperOpened }: NftItemType) => {
  const classes = useInputAndDisplayNftsStyles();
  const { safe } = useSafe();
  const [, stacksNetwork] = useNetwork();
  const [image, setImage] = useState('');
  const nftImagesStore = useNftImages();

  useEffect(() => {
    getNftImageById(collectionContract, nftId, safe.address, stacksNetwork, nftImagesStore).then(
      (res) => setImage(res?.image || '')
    );
  }, [nftId]);

  return (
    <div
      key={uniqueId(nftId)}
      className={classNames(
        classes.idCardWrapper,
        isWrapperOpened ? classes.idCardWrapperOpened : ''
      )}
    >
      <div className={classes.imagePlaceholder}>
        <img width={56} height={56} src={image} alt="" className={classes.nftImage} />
      </div>
      <div className={classes.idNftWrapper}>
        <span>#{nftId}</span>
        <img
          onClick={() => onDeleteClick(nftId)}
          className={classes.delete}
          src={DeleteIcon}
          alt=""
        />
      </div>
    </div>
  );
};

const useNftIdItemOnlyOneStyles = makeStyles<Theme>((theme) => ({
  idCardWrapper: {
    borderRadius: '8px',
    border: '1px solid var(--700, #34353B)',
    background: 'var(--750, #292A2F)',
    width: '100%',
    padding: '8px 16px',
    display: 'flex',
    alignItems: 'center',
    height: 72,
    gap: 8,
    marginTop: 13,
  },
  imagePlaceholder: {
    overflow: 'hidden',
    width: 40,
    height: 40,
    background: `url(${NftImagePlaceholder}) no-repeat center center, #41434B`,
    border: '1px solid #41434B',
    borderRadius: 8,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    color: '#FAFAFA',
    fontSize: '14px',
    fontWeight: 500,
    lineHeight: '20px',
  },
  delete: {
    marginLeft: 'auto',
    cursor: 'pointer',
  },
}));

// TODO_R: move into InputAndDisplayNfts/components
const NftIdItemOnlyOne = ({ nftId, collectionContract, onDelete }: NftItemOnlyOneType) => {
  const classes = useNftIdItemOnlyOneStyles();
  const { safe } = useSafe();
  const [, stacksNetwork] = useNetwork();
  const [image, setImage] = useState<NFTImage>();
  const nftImagesStore = useNftImages();

  useEffect(() => {
    if (!nftId) return;
    getNftImageById(collectionContract, nftId, safe.address, stacksNetwork, nftImagesStore).then(
      (res) => {
        if (res) setImage(res);
      }
    );
  }, [nftId]);

  return (
    <>
      {image && (
        <div className={classes.idCardWrapper}>
          <div className={classes.imagePlaceholder}>
            <ImageWithPlaceholder
              width={40}
              height={40}
              src={image?.image}
              alt=""
              className={classes.nftImage}
            />
          </div>
          <span className={classes.title}>#{image?.title}</span>
          <img onClick={() => onDelete(nftId)} className={classes.delete} src={DeleteIcon} alt="" />
        </div>
      )}
    </>
  );
};

type InputAndDisplayNftsProps = {
  addOrDeleteIdHandler: (id: string[]) => void;
  onDeleteClick: (id: string) => void;
  selectedNFTIds: string[];
  selectedCollectionId: string;
  nftImagesDropdownOpened: boolean;
  setNFTImagesDropdownOpened: Dispatch<SetStateAction<boolean>>;
};

// TODO_R: move into SendNftDialog/components
// TODO_R: [FRS]
// TODO_R: [RP] NFTImagesSelect
export const InputAndDisplayNfts = ({
  addOrDeleteIdHandler,
  onDeleteClick,
  selectedNFTIds,
  selectedCollectionId,
  nftImagesDropdownOpened,
  setNFTImagesDropdownOpened,
}: InputAndDisplayNftsProps) => {
  const classes = useInputAndDisplayNftsStyles();
  const [loading, setLoading] = useState(false);
  const [loadedNFTImages, setLoadedNFTImages] = useState<NFTImage[]>([]);
  const { safe } = useSafe();
  const [, stacksNetwork] = useNetwork();
  const address = useAddress();
  const [collectionContract] = selectedCollectionId.split('::');
  // TODO_R: логика с idsToDisplay слишком усложненна и не читабельна. Нужно ее переработать
  const [idsToDisplay, setIdsToDisplay] = useState(4);
  const [balance, setBalance] = useState<AddressBalance>();
  const [allNFTIds, setAllNFTIds] = useState<string[]>([]);
  const nftImagesStore = useNftImages();
  const [currentPage, setCurrentPage] = useState<number>(0);

  const loadCollectionPaginatedNFTsData = async (balance: AddressBalance, page: number) => {
    const data = (await getCollectionPaginatedNFTsData(
      selectedCollectionId,
      ITEMS_PER_PAGE.toString(),
      (page * ITEMS_PER_PAGE).toString(),
      address,
      safe.address,
      stacksNetwork,
      balance,
      nftImagesStore,
      true
    )) as NFTImage[];
    if (!data || !isArray(data)) return;
    setLoadedNFTImages((previousLoadedNFTImages) => [...previousLoadedNFTImages, ...data]);
  };

  useEffect(() => {
    if (!selectedCollectionId) return;
    const getData = async () => {
      const balance = await getAccountBalances(stacksNetwork, safe.address);
      const allNFTIds = await getAllCollectionNftIds(
        safe.address,
        stacksNetwork,
        selectedCollectionId
      );
      await loadCollectionPaginatedNFTsData(balance, 0);
      setAllNFTIds(allNFTIds);
      setBalance(balance);
    };
    setLoading(true);
    getData().finally(() => setLoading(false));
  }, [selectedCollectionId]);

  useEffect(() => {
    setLoadedNFTImages([]);
  }, [selectedCollectionId]);

  const currentLoadedNFTImagesCount = () => {
    if (!balance || !selectedCollectionId) return 0;

    return +balance.non_fungible_tokens[selectedCollectionId].count;
  };

  const addOrDeleteAllIds = () => {
    if (currentLoadedNFTImagesCount() === selectedNFTIds.length) return addOrDeleteIdHandler([]);
    addOrDeleteIdHandler(allNFTIds);
  };

  const handleDropdownOpen = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    event.stopPropagation();
    if (selectedCollectionId) setNFTImagesDropdownOpened((prevState) => !prevState);
  };

  return (
    <>
      {!BATCH_TRANSFER_DISABLED && selectedNFTIds.length > 0 && (
        <div className={classes.labelWrapper}>
          <span className={classes.labelSelectedAmount}>{selectedNFTIds.length} Selected</span>
        </div>
      )}
      {isEmpty(selectedNFTIds) && (
        <div className={classes.nftInput}>
          <div
            className={classNames(classes.inputWrapper, !selectedCollectionId && classes.emptyLst)}
            onClick={handleDropdownOpen}
          >
            <div onClick={handleDropdownOpen}>
              <InputField
                value={
                  selectedNFTIds.length
                    ? `#${selectedNFTIds.reduce((acc: string, item) => `${acc}, #${item}`)}`
                    : ''
                }
                placeHolder="Choose NFT"
                type="text"
                disabled
              />
            </div>
            <div
              className={classNames(
                classes.dropdown,
                nftImagesDropdownOpened && classes.dropdownOpen
              )}
            >
              {!BATCH_TRANSFER_DISABLED && (
                <div
                  className={classes.nftItem}
                  key={idsToDisplay.toString()}
                  onClick={() => addOrDeleteAllIds()}
                >
                  <div
                    className={classNames(
                      classes.checkBox,
                      currentLoadedNFTImagesCount() === selectedNFTIds.length
                        ? classes.checkBoxChecked
                        : ''
                    )}
                  ></div>
                  <span className={classNames(classes.dropDownTitle, classes.selectAllTitle)}>
                    Select All ({currentLoadedNFTImagesCount()})
                  </span>
                </div>
              )}
              {loading && (
                <img
                  src={LoadingSpinner}
                  className={classes.loader}
                  width={32}
                  height={32}
                  alt=""
                />
              )}
              {!loading &&
                loadedNFTImages?.map((nftImage) => (
                  <div
                    className={classes.nftItem}
                    key={nftImage.id}
                    onClick={() => addOrDeleteIdHandler([nftImage.id])}
                  >
                    {!BATCH_TRANSFER_DISABLED && (
                      <div
                        className={classNames(
                          classes.checkBox,
                          selectedNFTIds.some((selectedNFTId) => selectedNFTId === nftImage.id)
                            ? classes.checkBoxChecked
                            : ''
                        )}
                      />
                    )}
                    {nftImage.isVideo ? (
                      <video
                        style={{
                          objectFit: 'cover',
                        }}
                        className={classes.dropDownimg}
                        width={40}
                        height={40}
                        src={nftImage.image}
                      />
                    ) : (
                      <ImageWithPlaceholder
                        src={nftImage.image}
                        alt=""
                        width={40}
                        height={40}
                        className={classes.dropDownimg}
                      />
                    )}
                    <span className={classes.dropDownTitle}>#{nftImage.title}</span>
                    <span className={classes.dropDownId}>#{nftImage.id}</span>
                  </div>
                ))}
              {!loading && currentLoadedNFTImagesCount() > loadedNFTImages.length && (
                <button
                  className={classes.showMore}
                  onClick={() => {
                    if (balance) {
                      loadCollectionPaginatedNFTsData(balance, currentPage + 1);
                      setCurrentPage(currentPage + 1);
                    }
                  }}
                >
                  Show more
                </button>
              )}
            </div>
          </div>
          <span className={classes.nftInputAddButton}>
            <img
              src={ArrowDown}
              alt=""
              className={classNames(
                classes.inputArrow,
                nftImagesDropdownOpened ? classes.inputArrowOpened : ''
              )}
              onClick={handleDropdownOpen}
            />
          </span>
        </div>
      )}
      {!loading && selectedNFTIds.length > 0 && (
        <div
          className={classNames(
            classes.displaySelectedNftsWrapper,
            idsToDisplay > 4 ? classes.displaySelectedNftsWrapperOpened : ''
          )}
        >
          <div
            className={classNames(
              classes.inputedIdsList,
              idsToDisplay > 4 ? classes.inputedIdsListOpened : ''
            )}
          >
            {BATCH_TRANSFER_DISABLED ? (
              <NftIdItemOnlyOne
                nftId={selectedNFTIds[0]}
                onDelete={onDeleteClick}
                collectionContract={collectionContract}
              />
            ) : (
              selectedNFTIds
                .slice(0, idsToDisplay)
                .map((nftId: string) => (
                  <NftIdItem
                    nftId={nftId}
                    collectionContract={collectionContract}
                    onDeleteClick={onDeleteClick}
                    isWrapperOpened={idsToDisplay > 4}
                  />
                ))
            )}
          </div>
          {currentLoadedNFTImagesCount() > idsToDisplay && selectedNFTIds.length > idsToDisplay ? (
            <button
              className={classes.showMoreIds}
              onClick={() => setIdsToDisplay(selectedNFTIds.length)}
            >
              Show more
            </button>
          ) : (
            selectedNFTIds.length >= idsToDisplay && (
              <button className={classes.showMoreIds} onClick={() => setIdsToDisplay(4)}>
                Hide
              </button>
            )
          )}
        </div>
      )}
    </>
  );
};
